import { sleep } from '@takeshape/util';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { RowData } from '../../../common/daisy/data-table.js';
import { showMessage } from '../../../messages/actions.js';
import { useAgentEvalStore } from '../../agent-eval-store.js';

export type EvalTableRow = RowData & {
  name: string;
  agents: string[];
};

export const rowData: EvalTableRow[] = [
  {
    id: '1',
    name: 'Accuracy Assessment',
    agents: []
  },
  {
    id: '2',
    name: 'Safety Check',
    agents: ['chat', 'chat2', 'chat', 'chat2', 'chat', 'chat2']
  },
  {
    id: '3',
    name: 'Response Relevance',
    agents: ['chat']
  }
];

export const useAgentEvals = () => {
  const dispatch = useDispatch();
  const setRunEditOpen = useAgentEvalStore((state) => state.setRunEditOpen);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const { data, isFetching, isRefetching, isPreviousData, error, refetch } = useQuery({
    queryKey: ['evals', filter],
    queryFn: async () => {
      const rows = rowData.filter((row) => row.name.toLowerCase().includes(filter));
      await sleep(500);
      return {
        nodes: rows,
        pageInfo: {
          endCursor: undefined,
          hasNextPage: false
        }
      };
    },
    keepPreviousData: true,
    refetchOnMount: 'always'
  });

  const { mutate: handleDeleteSelected } = useMutation({
    mutationFn: async () => {
      // TODO
    },
    onSuccess() {
      dispatch(showMessage('info', `Deleted ${selected.join(', ')}`, 5000));
      void refetch();
    },
    onError() {
      dispatch(showMessage('error', `Error deleting ${selected.join(', ')}`));
    }
  });

  const handleCreate = useCallback(() => {
    setRunEditOpen({ createMode: true });
  }, [setRunEditOpen]);

  const handleEditRun = useCallback(
    (evalId: string) => {
      setRunEditOpen({ createMode: false, evalId });
    },
    [setRunEditOpen]
  );

  return {
    filter,
    setFilter,
    handleCreate,
    error,
    selected,
    setSelected,
    rows: data?.nodes,
    loading: isRefetching || (isFetching && (!data || isPreviousData)),
    handleDeleteSelected,
    handleEditRun
  };
};

export type AgentEvalsProps = ReturnType<typeof useAgentEvals>;
